import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
    DataSources,
    AppointmentsClient,
    AggregateType,
    AuthService,
    GetAppointmentComplianceScoreQuery,
    GetAppointmentComplianceTrendingQuery,
    GetAppointmentSupplierComplianceTrendingQuery,
    ExportAppointmentComplianceQuery,
    ITrendingQuery,
    PermissionType,
    AccessType,
    ScoreDto,
} from '@services';
import {
    DxButtonComponent,
    DxDateRangeBoxComponent,
    DxSelectBoxComponent,
    DxTagBoxModule,
    DxPieChartModule,
    DxChartModule,
} from 'devextreme-angular';
import { FileUtils } from 'src/app/shared/file-utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import EnumUtils from 'src/app/shared/enum-utils';
import { TrendingComponent } from 'src/app/components/library/trending/trending.component';
import { NoData, noDataColor, noDataRing, scoreCenterLabel } from 'src/app/components/library/doughnut-chart/score-center';

type ScoreDetail = { argument: string; value: number };

export function complianceRing(score: ScoreDto): ScoreDetail[] {
    if (!score.totals?.['Appointments Completed'])
        return [];

    const percent = score.avgScorePercent ?? 0;
    return [
        { argument: 'Compliance %', value: percent },
        { argument: 'Remaining', value: Math.max(0, 100 - percent) },
    ];
}

export function complianceRingPalette(): Record<string, string> {
    return {
        ['Compliance %']: 'var(--bs-primary)',
        ['Remaining']: 'rgba(100, 100, 100, 0.2)',
    };
}

@Component({
    selector: 'app-appointment-compliance-score',
    standalone: true,
    imports: [
        CommonModule,
        DxPieChartModule,
        DxDateRangeBoxComponent,
        DxButtonComponent,
        DxSelectBoxComponent,
        DecimalPipe,
        DxTagBoxModule,
        TrendingComponent,
        DxChartModule,
    ],
    templateUrl: './appointment-compliance-score.component.html',
})
export class AppointmentComplianceScoreComponent implements OnInit {
    private appointmentsClient = inject(AppointmentsClient);
    private destroyRef = inject(DestroyRef);
    private dataSources = inject(DataSources);
    private auth = inject(AuthService);
    private decimalPipe = new DecimalPipe('en-US');

    protected supplierDataSource = this.dataSources.appointmentComplianceSupplierOptions();

    selectedSuppliers: string[] = [];
    daysLookBack: number = 0;
    canFilterBySupplier: boolean = false;

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    scoreRingData: ScoreDetail[] = [];
    centerLabel = '';
    scoreDetails: ScoreDetail[] = [];
    private readonly ringPalette = complianceRingPalette();

    supplierBars: ScoreDetail[] = [];

    ngOnInit(): void {
        this.initDateRange();

        this.canFilterBySupplier = this.auth.checkPermission(PermissionType.AppointmentComplianceScore, AccessType.Manage);

        this.loadScore();
        this.loadSupplierBreakdown();
    }

    loadTrendingData = (query: ITrendingQuery) => {
        return this.appointmentsClient.getAppointmentComplianceTrending(new GetAppointmentComplianceTrendingQuery({
            from: query.from,
            to: query.to,
            aggregateType: query.aggregateType,
            supplierIds: this.selectedSuppliers?.length ? this.selectedSuppliers : undefined,
        }));
    }

    get trendingKey(): string {
        return this.selectedSuppliers.join(',');
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    get canSeeSupplierCompliance() {
        return this.auth.checkPermission(PermissionType.AppointmentSupplierComplianceScore, AccessType.ViewOnly);
    }

    protected onFiltersChanged(): void {
        this.loadScore();
    }

    protected onDateRangeChanged(): void {
        this.loadScore();
        this.loadSupplierBreakdown();
    }

    onExport() {
        const query = new ExportAppointmentComplianceQuery({
            supplierIds: this.selectedSuppliers?.length ? this.selectedSuppliers : undefined,
            from: this.fromDate,
            to: this.toDate,
        });

        this.appointmentsClient
            .exportAppointmentCompliance(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }

    customizeScoreRingPoint = (pointInfo: { argument: string }) => ({
        color: pointInfo.argument === NoData ? noDataColor : this.ringPalette[pointInfo.argument],
    });

    customizeSupplierTooltip = (point: { value: number }) => ({
        text: `${this.decimalPipe.transform(point.value, '1.0-2')}%`,
    });

    private loadScore(): void {
        if (!this.fromDate || !this.toDate) return;

        this.appointmentsClient
            .getAppointmentComplianceScore(new GetAppointmentComplianceScoreQuery({
                from: this.fromDate,
                to: this.toDate,
                supplierIds: this.selectedSuppliers?.length ? this.selectedSuppliers : undefined,
            }))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                const totals = dto.totals ?? {};

                this.scoreDetails = [
                    { argument: 'Appointments Completed', value: totals['Appointments Completed'] ?? 0 },
                    { argument: 'Compliance Score', value: totals['Compliance Score'] ?? 0 },
                    { argument: 'Compliance %', value: totals['Compliance %'] ?? 0 },
                ];

                const ring = complianceRing(dto);
                this.scoreRingData = ring.length ? ring : noDataRing();
                this.centerLabel = scoreCenterLabel(ring.length > 0, dto.avgScorePercent, 'percent');
            });
    }

    private loadSupplierBreakdown(): void {
        if (!this.fromDate || !this.toDate) return;

        this.appointmentsClient
            .getAppointmentSupplierComplianceTrending(new GetAppointmentSupplierComplianceTrendingQuery({
                from: this.fromDate,
                to: this.toDate,
            }))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                this.supplierBars = Object.entries(dto.compliancePercentBySupplier ?? {})
                    .map(([supplier, percent]) => ({ argument: supplier, value: percent }))
                    .sort((a, b) => a.value - b.value);
            });
    }

    private initDateRange(): void {
        if (this.daysLookBack > 0) {
            this.dateRangeFilterValue = [
                new Date(Date.now() - this.daysLookBack * 24 * 60 * 60 * 1000),
                new Date(),
            ];
        }
    }
}
